import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError, fetchAdminAds, updateAdminAds, adminDeleteResult, adminDeleteTheme, adminRestoreTheme, fetchAdminAccounts,
  adminDrawThemeImages, fetchAdminContacts, fetchAdminLimits, fetchAdminReports, resolveAdminReports, fetchAdminLogs, fetchAdminResults, fetchAdminStats, fetchAdminThemes, updateAdminAccount,
  updateAdminContact, updateAdminLimits,
} from '../api/client';
import {
  AD_PLACEMENTS, AFFILIATE_NETWORKS, REPORT_REASONS,
  type AdminAccount, type AdminAds, type AdminContact, type AdminLimits, type AdminLog, type AdminResult, type AdminStats,
  type AdminReportGroup, type AdminTheme, type AdPlacement, type AffiliateItem,
} from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { providerLabelOf } from '../auth/config';
import { imageStatusText } from '../components/ImageStatus';
import { VideosTab } from './AdminVideos';

/**
 * 管理メニュー（`/admin`、doc/AUTH.md §4）。管理者だけが開ける。
 * 見た目は凝らない。表と操作ボタンと確認ダイアログだけ。
 * 権限の確認はサーバ側（`/v1/admin/*`）で必ず行うので、ここでの出し分けは飾り。
 * 「広告」タブは広告を切ったサイトでも出す（準備してから入れられるように）。
 */
type Tab = 'limits' | 'themes' | 'videos' | 'reports' | 'accounts' | 'ads' | 'results' | 'contacts' | 'logs';

const TABS: readonly { readonly id: Tab; readonly label: string }[] = [
  { id: 'limits', label: '上限' },
  { id: 'themes', label: '診断' },
  { id: 'videos', label: '動画' },
  { id: 'reports', label: '通報' },
  { id: 'accounts', label: 'ユーザー' },
  { id: 'ads', label: '広告' },
  { id: 'results', label: '結果' },
  { id: 'contacts', label: '問い合わせ' },
  { id: 'logs', label: '記録' },
];

export function AdminPage() {
  const { ready, user, isAdmin, provider, enabled, busy, requestSignIn } = useAuth();
  const [tab, setTab] = useState<Tab>('themes');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    void fetchAdminStats().then(setStats).catch(() => undefined);
  }, [isAdmin]);

  if (!ready) return <p className="page__notice">読み込んでいます…</p>;

  if (!user) {
    return (
      <main className="page">
        <h1 className="page__title">管理メニュー</h1>
        <p className="page__lead">管理者のアカウントでログインしてください。管理者かどうかは Google ログインのメールアドレスで決まります。</p>
        <button className="button button--primary" type="button" onClick={() => void requestSignIn()} disabled={busy || !enabled}>
          {enabled ? 'ログインする' : 'ログイン準備中'}
        </button>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="page">
        <h1 className="page__title">管理メニュー</h1>
        <p className="page__lead">
          このアカウントには管理者の権限がありません。管理者は、環境変数 <code>ADMIN_EMAILS</code> に書かれたアドレスと、<strong>Google ログインの確認済みメールアドレス</strong>が一致した人だけです。
        </p>
        {provider && provider !== 'google.com' && (
          <p className="page__lead">
            いまは {providerLabelOf(provider)} でログインしています。管理者として入るには、いちどログアウトして Google でログインし直してください。
          </p>
        )}
        <Link className="button button--ghost" to="/">トップへ戻る</Link>
      </main>
    );
  }

  return (
    <main className="page admin">
      <h1 className="page__title">管理メニュー</h1>
      {stats && (
        <ul className="admin__stats" aria-label="今日と直近 30 日">
          <StatCard label="絵の生成（回）" today={stats.imageRuns.today} month={stats.imageRuns.month} />
          <StatCard label="描いた枚数" today={stats.imagesDrawn.today} month={stats.imagesDrawn.month} />
          <StatCard label="作られた診断" today={stats.themesCreated.today} month={stats.themesCreated.month} />
          <StatCard label="受けた診断" today={stats.diagnoses.today} month={stats.diagnoses.month} />
        </ul>
      )}
      {error && <p className="page__error" role="alert">{error}</p>}

      <div className="admin__tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.id}
            role="tab"
            aria-selected={tab === item.id}
            className={`chip${tab === item.id ? ' chip--on' : ''}`}
            type="button"
            onClick={() => { setTab(item.id); setError(''); }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'limits' && <LimitsTab onError={setError} />}
      {tab === 'themes' && <ThemesTab onError={setError} />}
      {tab === 'videos' && <VideosTab onError={setError} />}
      {tab === 'reports' && <ReportsTab onError={setError} />}
      {tab === 'accounts' && <AccountsTab onError={setError} />}
      {tab === 'ads' && <AdsTab onError={setError} />}
      {tab === 'results' && <ResultsTab onError={setError} />}
      {tab === 'contacts' && <ContactsTab onError={setError} />}
      {tab === 'logs' && <LogsTab onError={setError} />}
    </main>
  );
}

function StatCard({ label, today, month }: { readonly label: string; readonly today: number; readonly month: number }) {
  return (
    <li className="admin__stat">
      <span className="admin__stat-label">{label}</span>
      <span className="admin__stat-today">{today}</span>
      <span className="admin__stat-month">30 日で {month}</span>
    </li>
  );
}

const fail = (cause: unknown, fallback: string) => (cause instanceof ApiError ? cause.message : fallback);

/**
 * 上限（doc/AUTH.md §15）と料金の見積もり。
 *
 * - サイト全体の 1 日の生成数（**お金の元栓**）。人数に関係なくその日の合計で止め、管理者にも効く。既定は診断 100・絵 50。
 * - 1 人あたりの 1 日の回数。ログインしていない人の診断（既定 3）・ログインした人の診断（既定は上限なし）・
 *   ログインした人の画像つき診断（既定 3）。管理者は数えない。「ユーザー」タブの個別上限があればそちらが先。
 * - 料金の見積もりは API（services/api/src/lib/costs.ts）が単価と実測のトークン数から出す。請求額ではない。
 * 空欄にすると「上限なし」（ログインしていない人の回数は空欄にできない）、0 にすると止まる。
 */
type LimitField =
  | 'themeDailyLimit' | 'imageDailyLimit' | 'videoDailyLimit' | 'compatDailyLimit' | 'compatImageDailyLimit'
  | 'anonThemeDailyLimit' | 'userThemeDailyLimit' | 'userImageDailyLimit' | 'userCompatDailyLimit' | 'userCompatImageDailyLimit';
const LIMIT_FIELDS: readonly LimitField[] = [
  'themeDailyLimit', 'imageDailyLimit', 'videoDailyLimit', 'compatDailyLimit', 'compatImageDailyLimit',
  'anonThemeDailyLimit', 'userThemeDailyLimit', 'userImageDailyLimit', 'userCompatDailyLimit', 'userCompatImageDailyLimit',
];

function LimitsTab({ onError }: { readonly onError: (message: string) => void }) {
  const [limits, setLimits] = useState<AdminLimits | null>(null);
  const [values, setValues] = useState<Record<LimitField, string>>({
    themeDailyLimit: '', imageDailyLimit: '', videoDailyLimit: '', compatDailyLimit: '', compatImageDailyLimit: '',
    anonThemeDailyLimit: '', userThemeDailyLimit: '', userImageDailyLimit: '', userCompatDailyLimit: '', userCompatImageDailyLimit: '',
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');

  const apply = useCallback((value: AdminLimits) => {
    setLimits(value);
    setValues(Object.fromEntries(LIMIT_FIELDS.map((f) => [f, value[f] === null ? '' : String(value[f])])) as Record<LimitField, string>);
  }, []);

  useEffect(() => {
    void fetchAdminLimits().then(apply).catch((cause: unknown) => onError(fail(cause, '上限を読み込めませんでした。')));
  }, [apply, onError]);

  if (!limits) return <p className="page__notice">読み込んでいます…</p>;

  const save = () => {
    setBusy(true);
    setSaved('');
    const patch = Object.fromEntries(
      LIMIT_FIELDS
        // ログインしていない人の回数は「上限なし」にできないので、空欄なら送らない（据え置き）
        .filter((f) => !(f === 'anonThemeDailyLimit' && values[f].trim() === ''))
        .map((f) => [f, values[f].trim() === '' ? null : Number(values[f])]),
    );
    void updateAdminLimits(patch)
      .then((next) => { apply(next); setSaved('保存しました。'); })
      .catch((cause: unknown) => onError(fail(cause, '保存できませんでした。')))
      .finally(() => setBusy(false));
  };

  const field = (f: LimitField, label: string, placeholder = '空欄なら上限なし') => (
    <label className="field">
      <span className="field__label">{label}</span>
      <input className="field__input" type="number" min={0} inputMode="numeric" value={values[f]} disabled={busy}
        placeholder={placeholder} onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))} />
    </label>
  );

  return (
    <section className="admin__panel">
      <h2 className="section__title">サイト全体</h2>
      <p className="page__lead">
        サイト全体で <strong>1 日に作れる数</strong> です。人数に関係なく、その日の合計で止まります。
        1 人あたりの回数とは別勘定で、<strong>管理者にも効きます</strong>。
      </p>

      <ul className="admin__stats" aria-label="今日の使用状況">
        <UsageCard label="診断の生成" quota={limits.usage.theme} />
        <UsageCard label="絵の生成（回）" quota={limits.usage.image} />
        <UsageCard label="動画（本）" quota={limits.usage.video} />
        <UsageCard label="相性の AI の読み" quota={limits.usage.compat} />
        <UsageCard label="相性のエピソードの絵（回）" quota={limits.usage.compatImage} />
      </ul>

      <div className="admin__limits">
        {field('themeDailyLimit', '1 日に作れる診断の数')}
        {field('imageDailyLimit', '1 日に絵を描ける回数（1 回でキャラの人数ぶん）')}
        {field('videoDailyLimit', '1 日に作れる動画の本数（「動画」タブ。1 本で数ドル）')}
        {field('compatDailyLimit', '1 日に書ける相性の AI の読み（Gemini 4 Argon。読み済みの組み合わせは数えない）')}
        {field('compatImageDailyLimit', '1 日に描ける相性のエピソードの絵（1 回で 3 枚。描き済みの組み合わせは数えない）')}
      </div>

      <h2 className="section__title">1 人あたり（1 日）</h2>
      <p className="page__lead">
        1 人が使いすぎないための回数です。<strong>管理者は数えません</strong>。画像つき診断の回数は「ユーザー」タブで人ごとに上書きできます。
      </p>
      <div className="admin__limits">
        {field('anonThemeDailyLimit', 'ログインしていない人の診断（ブラウザごと。0 でログイン必須）', '空欄にはできません')}
        {field('userThemeDailyLimit', 'ログインした人の診断（画像の有無を問わず）')}
        {field('userImageDailyLimit', 'ログインした人の画像つき診断（絵を描ける回数）')}
        {field('userCompatDailyLimit', 'ログインした人の相性の AI の読み（関係を選んで読む回数）')}
        {field('userCompatImageDailyLimit', 'ログインした人の相性のエピソードの絵（1 回で 3 枚）')}
      </div>

      <div className="page__actions">
        <button className="button button--primary" type="button" onClick={save} disabled={busy}>
          {busy ? '保存しています…' : '保存する'}
        </button>
        {saved && <span className="admin__saved" role="status">{saved}</span>}
      </div>

      <p className="page__footnote">
        空欄は「上限なし」、<strong>0 は止める</strong>です。絵を止めても診断そのものは作れます。戻るのは日付が変わるとき（日本時間の 0 時）。
        {limits.source === 'env'
          ? '　いまは環境変数の既定値が効いています。'
          : `　${limits.updatedAt ? new Date(limits.updatedAt).toLocaleString('ja-JP') : ''} に ${limits.updatedBy === 'budget' ? '予算の自動停止' : limits.updatedBy ?? '管理者'} が変えました。`}
      </p>

      <CostPanel limits={limits} />
    </section>
  );
}

/** ドルを円の概算にする。10 円未満は小数 1 桁まで。 */
function yen(usd: number, rate: number): string {
  const value = usd * rate;
  return value < 10 ? `約 ${value.toFixed(1)} 円` : `約 ${Math.round(value).toLocaleString('ja-JP')} 円`;
}
const dollars = (usd: number) => `$${usd < 1 ? usd.toFixed(3) : usd.toFixed(2)}`;

/** 料金の見積もり（lib/costs.ts）。1 回あたり・1 日の最大・今日ここまで。 */
function CostPanel({ limits }: { readonly limits: AdminLimits }) {
  const { cost } = limits;
  const rate = cost.usdJpy;
  return (
    <div className="admin__cost">
      <h2 className="section__title">料金の見積もり</h2>
      <p className="page__lead">
        生成 1 回あたりのおおよその料金です。<strong>請求額ではありません</strong>（正は Cloud Billing の SKU 別レポート）。費用のほとんどは絵の枚数で決まります。
      </p>

      <ul className="admin__stats" aria-label="今日と 1 日の最大">
        <li className="admin__stat">
          <span className="admin__stat-label">今日ここまで（概算）</span>
          <span className="admin__stat-today">{yen(cost.today.usd, rate)}</span>
          <span className="admin__stat-month">診断 {cost.today.themes} 件・絵 {cost.today.imagesDrawn} 枚</span>
        </li>
        <li className="admin__stat">
          <span className="admin__stat-label">1 日の最大（16 人で上限まで）</span>
          <span className="admin__stat-today">{cost.dailyMax.typical === null ? '上限なし' : yen(cost.dailyMax.typical, rate)}</span>
          <span className="admin__stat-month">{cost.dailyMax.largest === null ? 'サイト全体の上限を置くと出ます' : `全部 64 人なら ${yen(cost.dailyMax.largest, rate)}`}</span>
        </li>
      </ul>

      <table className="admin__table admin__cost-table">
        <caption className="admin__cost-caption">診断 1 件あたり</caption>
        <thead>
          <tr><th scope="col">キャラ数</th><th scope="col">画像なし（文章だけ）</th><th scope="col">画像つき</th></tr>
        </thead>
        <tbody>
          {cost.perTheme.map((row) => (
            <tr key={row.characters}>
              <th scope="row">{row.characters} 人{row.characters === 16 ? '（既定）' : ''}</th>
              <td>{yen(row.textOnly, rate)} <span className="admin__cost-usd">{dollars(row.textOnly)}</span></td>
              <td>{yen(row.withImages, rate)} <span className="admin__cost-usd">{dollars(row.withImages)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>

      <ul className="page__footnote admin__cost-notes">
        <li>
          文章: <code>{cost.prices.textModel}</code> 入力 ${cost.prices.textInputPerM}・出力 ${cost.prices.textOutputPerM}（100 万トークンあたり）。トークン数は本番ログの実測（16 人で入力 約 5,000・出力 約 6,000）。JSON が壊れて作り直した回は約 2 倍。
          {cost.prices.nextTextPrice && ` ${cost.prices.nextTextPrice.from} から入力 $${cost.prices.nextTextPrice.inputPerM}・出力 $${cost.prices.nextTextPrice.outputPerM} に上がります。`}
        </li>
        <li>絵: <code>{cost.prices.imageModel}</code> 1 枚 ${cost.prices.imagePerImage}（出力 1,120 トークン）。描けなかった絵は 1 回だけ描き直します。絵を頼むプロンプトの入力分は 1 円未満なので省いています。</li>
        <li>1 日の最大は、サイト全体の上限まで診断を作り、絵を上限の回数まで描き、相性のエピソードの絵（1 回 3 枚）も上限まで描いたとき。今日の概算は、診断を 16 人として数えています。円は 1 ドル {rate} 円で換算。</li>
      </ul>
    </div>
  );
}

function UsageCard({ label, quota }: { readonly label: string; readonly quota: AdminLimits['usage']['theme'] }) {
  return (
    <li className="admin__stat">
      <span className="admin__stat-label">{label}</span>
      <span className="admin__stat-today">{quota.used}{quota.limit === null ? '' : ` / ${quota.limit}`}</span>
      <span className="admin__stat-month">{quota.limit === null ? '上限なし' : `今日あと ${quota.remaining}`}</span>
    </li>
  );
}

function ThemesTab({ onError }: { readonly onError: (message: string) => void }) {
  const [themes, setThemes] = useState<readonly AdminTheme[] | null>(null);
  const [query, setQuery] = useState('');
  const [working, setWorking] = useState('');

  const load = useCallback(async (q = query) => {
    try {
      setThemes((await fetchAdminThemes(q)).themes);
    } catch (cause) {
      onError(fail(cause, '診断の一覧を読み込めませんでした。'));
    }
  }, [onError, query]);

  useEffect(() => { void load(''); }, [load]);

  const act = async (themeId: string, run: () => Promise<unknown>) => {
    setWorking(themeId);
    try {
      await run();
      await load();
    } catch (cause) {
      onError(fail(cause, 'できませんでした。'));
    } finally {
      setWorking('');
    }
  };

  return (
    <section className="admin__panel">
      <form
        className="admin__search"
        onSubmit={(event) => { event.preventDefault(); void load(); }}
      >
        <input className="field__input" value={query} placeholder="題名・テーマの文で絞る" onChange={(event) => setQuery(event.target.value)} />
        <button className="button button--ghost button--small" type="submit">絞る</button>
      </form>
      {themes === null ? <p className="page__notice">読み込んでいます…</p> : (
        // 表にすると列が細くなって横にはみ出すので、1 件 1 枚のカードにして折り返す
        <ul className="admin__themes">
          {themes.map((theme) => {
            const busy = working === theme.themeId;
            const status = theme.images?.status ?? null;
            const drawing = status === 'pending' || status === 'running';
            const drawn = status === 'done';
            return (
              <li key={theme.themeId} className={`admin__theme${theme.deletedAt ? ' admin__row--deleted' : ''}`}>
                <div className="admin__theme-head">
                  <Link className="admin__theme-title" to={theme.href}>{theme.title}</Link>
                  <span className="admin__sub">{new Date(theme.createdAt).toLocaleString('ja-JP')} ／ {theme.typeCount} タイプ ／ {theme.generatedBy}</span>
                  {theme.prompt && <span className="admin__sub admin__prompt">{theme.prompt}</span>}
                </div>
                <dl className="admin__theme-meta">
                  <div><dt>作った人</dt><dd>{theme.createdBy === 'import' ? '運営（取り込み）' : theme.owner ?? '（ログインなし）'}</dd></div>
                  <div><dt>受けた</dt><dd>{theme.plays}</dd></div>
                  <div><dt>絵</dt><dd>{imageStatusText(theme.images)}</dd></div>
                </dl>
                <div className="admin__actions">
                  {theme.deletedAt ? (
                    <>
                      <button className="button button--ghost button--small" type="button" disabled={busy}
                        onClick={() => void act(theme.themeId, () => adminRestoreTheme(theme.themeId))}>戻す</button>
                      <button className="button button--danger button--small" type="button" disabled={busy}
                        onClick={() => {
                          if (!window.confirm(`「${theme.title}」を完全に消します。絵も消え、元に戻せません。よろしいですか？`)) return;
                          void act(theme.themeId, () => adminDeleteTheme(theme.themeId, { purge: true }));
                        }}>完全に消す</button>
                    </>
                  ) : (
                    <>
                      {/* 他の人の診断に絵を描けるのはここだけ（入口ページでは本人だけ）。サイト全体の絵の上限を 1 回使う */}
                      <button className="button button--ghost button--small" type="button" disabled={busy || drawing}
                        onClick={() => {
                          const verb = drawn ? '描き直します（できている絵は捨てます）' : '描きます';
                          if (!window.confirm(`「${theme.title}」のキャラ ${theme.characterCount} 人の絵を${verb}。サイト全体の絵の上限を 1 回使います。`)) return;
                          void act(theme.themeId, () => adminDrawThemeImages(theme.themeId, drawn));
                        }}>{drawing ? '描いています…' : drawn ? '絵を描き直す' : '絵を描く'}</button>
                      {/* 描けていない絵だけ（白雪姫のように、名前で有名な映画の意匠と見なされて断られるもの）を、名前を出さずにオリジナルの見た目で描く */}
                      {status === 'partial' && theme.images && theme.images.done < theme.images.total && (
                        <button className="button button--ghost button--small" type="button" disabled={busy || drawing}
                          onClick={() => {
                            const missing = theme.images ? theme.images.total - theme.images.done : 0;
                            if (!window.confirm(`「${theme.title}」の描けていない ${missing} 人の絵を、版権に気をつけて描きます（名前を出さず、有名な映像化作品の意匠を避けたオリジナルの見た目で）。サイト全体の絵の上限を 1 回使います。`)) return;
                            void act(theme.themeId, () => adminDrawThemeImages(theme.themeId, false, true));
                          }}>描けていない絵を版権に気をつけて描く</button>
                      )}
                      <button className="button button--danger button--small" type="button" disabled={busy}
                        onClick={() => {
                          if (!window.confirm(`「${theme.title}」を消します（あとで戻せます）。`)) return;
                          void act(theme.themeId, () => adminDeleteTheme(theme.themeId));
                        }}>消す</button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const reasonLabel = (id: string) => REPORT_REASONS.find((r) => r.id === id)?.label ?? id;

/**
 * 診断の通報（doc/ADS.md §5）。診断ごとにまとめて並べる。未対応の通報がある診断には、入口・結果に広告が出ない。
 * 問題があれば「消す」、無ければ「対応済みにする」（広告の止めが外れる）。消しても通報は未対応のまま残るので、あわせて対応済みにする。
 */
function ReportsTab({ onError }: { readonly onError: (message: string) => void }) {
  const [groups, setGroups] = useState<readonly AdminReportGroup[] | null>(null);
  const [minPlays, setMinPlays] = useState<number | null>(null);
  const [all, setAll] = useState(false);
  const [working, setWorking] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await fetchAdminReports(all);
      setGroups(response.groups);
      setMinPlays(response.minPlays);
    } catch (cause) {
      onError(fail(cause, '通報を読み込めませんでした。'));
    }
  }, [all, onError]);

  useEffect(() => { void load(); }, [load]);

  const act = async (themeId: string, run: () => Promise<unknown>) => {
    setWorking(themeId);
    try {
      await run();
      await load();
    } catch (cause) {
      onError(fail(cause, 'できませんでした。'));
    } finally {
      setWorking('');
    }
  };

  if (groups === null) return <p className="page__notice">読み込んでいます…</p>;
  return (
    <section className="admin__panel">
      <label className="field field--check">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
        <span>対応済みも出す</span>
      </label>
      {groups.length === 0 && <p className="page__notice">{all ? '通報はまだありません。' : '未対応の通報はありません。'}</p>}
      <ul className="admin__themes">
        {groups.map((group) => {
          const busy = working === group.themeId;
          return (
            <li key={group.themeId} className={`admin__theme${group.deletedAt ? ' admin__row--deleted' : ''}`}>
              <div className="admin__theme-head">
                {group.deletedAt ? <span className="admin__theme-title">{group.title}</span> : <Link className="admin__theme-title" to={group.href}>{group.title}</Link>}
                <span className="admin__sub">
                  {group.createdBy === 'import' ? '運営（取り込み）' : group.createdBy === 'user' ? 'ログインして作成' : 'ログインなしで作成'} ／ 受けた {group.plays} ／ 未対応の通報 {group.openReports}
                  {' ／ '}{group.deletedAt ? '削除済み' : group.adsAllowed ? '広告を出している' : '広告を止めている'}
                </span>
              </div>
              <ul className="admin__contacts">
                {group.reports.map((item) => (
                  <li key={item.reportId} className="admin__contact">
                    <p className="admin__contact-head">
                      <span className="chip">{reasonLabel(item.reason)}</span>
                      <span>{new Date(item.createdAt).toLocaleString('ja-JP')}</span>
                      {item.href && <Link to={item.href}>送った結果ページ</Link>}
                      {item.status === 'done' && <span className="badge badge--done">対応済み</span>}
                    </p>
                    {item.note && <p className="admin__contact-body">{item.note}</p>}
                  </li>
                ))}
              </ul>
              <div className="admin__actions">
                {group.openReports > 0 && (
                  <button className="button button--ghost button--small" type="button" disabled={busy}
                    onClick={() => {
                      const verb = group.deletedAt ? '対応済みにします。' : '問題なしとして対応済みにします。入口・結果に広告が出るようになります。';
                      if (!window.confirm(`「${group.title}」への通報を${verb}`)) return;
                      void act(group.themeId, () => resolveAdminReports(group.themeId));
                    }}>対応済みにする</button>
                )}
                {!group.deletedAt && (
                  <button className="button button--danger button--small" type="button" disabled={busy}
                    onClick={() => {
                      if (!window.confirm(`「${group.title}」を消して、通報を対応済みにします（消した診断は「診断」タブから戻せます）。`)) return;
                      void act(group.themeId, async () => {
                        await adminDeleteTheme(group.themeId);
                        await resolveAdminReports(group.themeId);
                      });
                    }}>消す</button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="page__footnote">
        通報はログインなしで送れ、送った人は記録していません。同じブラウザから同じ診断へは 1 日 1 件しか数えません。
        未対応の通報がある診断には広告を出しません。通報が無くても、利用者が作った診断に広告が出るのは受けた回数が {minPlays ?? '…'} 以上のものだけです（「広告」タブで変えられます）。
      </p>
    </section>
  );
}

function AccountsTab({ onError }: { readonly onError: (message: string) => void }) {
  const [accounts, setAccounts] = useState<readonly AdminAccount[] | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async () => {
    try {
      setAccounts((await fetchAdminAccounts()).accounts);
    } catch (cause) {
      onError(fail(cause, 'ユーザーを読み込めませんでした。'));
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  const act = async (uid: string, patch: { role?: 'user' | 'admin'; imageLimitOverride?: number | null }) => {
    setWorking(uid);
    try {
      await updateAdminAccount(uid, patch);
      await load();
    } catch (cause) {
      onError(fail(cause, '変えられませんでした。'));
    } finally {
      setWorking('');
    }
  };

  if (accounts === null) return <p className="page__notice">読み込んでいます…</p>;
  return (
    <section className="admin__panel">
      <table className="admin__table">
        <thead>
          <tr><th>メールアドレス</th><th>権限</th><th>由来</th><th>今日の回数</th><th>この人の上限</th><th>最終ログイン</th></tr>
        </thead>
        <tbody>
          {accounts.map((account) => (
            <tr key={account.uid}>
              <td>
                {account.email ?? '（取得できません）'}
                {account.name && <span className="admin__sub">{account.name}</span>}
                <span className="admin__sub admin__uid">{account.uid}</span>
              </td>
              <td>
                <button className="button button--ghost button--small" type="button" disabled={working === account.uid}
                  onClick={() => void act(account.uid, { role: account.role === 'admin' ? 'user' : 'admin' })}>
                  {account.role === 'admin' ? '管理者（外す）' : '一般（管理者にする）'}
                </button>
              </td>
              <td>
                {account.role !== 'admin' ? '' : account.roleSource === 'env' ? '環境変数' : '手で付与'}
              </td>
              <td>{account.usedToday}</td>
              <td>
                <input
                  className="field__input field__input--tiny"
                  type="number"
                  min={0}
                  defaultValue={account.imageLimitOverride ?? ''}
                  placeholder="既定"
                  disabled={working === account.uid}
                  onBlur={(event) => {
                    const raw = event.target.value.trim();
                    const next = raw === '' ? null : Number(raw);
                    if (next === (account.imageLimitOverride ?? null)) return;
                    void act(account.uid, { imageLimitOverride: next });
                  }}
                />
              </td>
              <td>{new Date(account.lastSeenAt).toLocaleString('ja-JP')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="page__footnote">
        メールアドレスは保存しておらず、この一覧を開いたときにログインの仕組み（Firebase）から読んで出しています（取得できないときは uid だけ）。
        上限を空にすると既定（「上限」タブの「ログインした人の画像つき診断」）に戻ります。管理者は回数を数えません。「環境変数」由来の管理者は <code>ADMIN_EMAILS</code> から外すと次のログインで権限が下がります。「手で付与」はここで外すまで残ります。
      </p>
    </section>
  );
}

function ResultsTab({ onError }: { readonly onError: (message: string) => void }) {
  const [results, setResults] = useState<readonly AdminResult[] | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async () => {
    try {
      setResults((await fetchAdminResults()).results);
    } catch (cause) {
      onError(fail(cause, '結果を読み込めませんでした。'));
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  if (results === null) return <p className="page__notice">読み込んでいます…</p>;
  return (
    <section className="admin__panel">
      <table className="admin__table">
        <thead>
          <tr><th>結果</th><th>診断</th><th>型</th><th>受けた日</th><th>操作</th></tr>
        </thead>
        <tbody>
          {results.map((result) => (
            <tr key={result.resultId}>
              <td><Link to={result.href}>{result.resultId}</Link></td>
              <td>{result.themeId}</td>
              <td>{result.typeCode}</td>
              <td>{new Date(result.createdAt).toLocaleString('ja-JP')}</td>
              <td>
                <button className="button button--danger button--small" type="button" disabled={working === result.resultId}
                  onClick={() => {
                    if (!window.confirm('この結果を消します。元に戻せません。')) return;
                    setWorking(result.resultId);
                    void adminDeleteResult(result.resultId)
                      .then(load)
                      .catch((cause: unknown) => onError(fail(cause, '消せませんでした。')))
                      .finally(() => setWorking(''));
                  }}>消す</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="page__footnote">結果には氏名・メールは入っていません（uid だけ）。通報があったときに使います。</p>
    </section>
  );
}

/**
 * 問い合わせ（doc/AUTH.md §14）。フォームから届いたものを新しい順に並べる。
 * 「対応済み」にすると**返信先が消える**ので、返信してから押す。
 */
function ContactsTab({ onError }: { readonly onError: (message: string) => void }) {
  const [contacts, setContacts] = useState<readonly AdminContact[] | null>(null);
  const [working, setWorking] = useState('');
  const [openOnly, setOpenOnly] = useState(true);

  const load = useCallback(async () => {
    try {
      setContacts((await fetchAdminContacts()).contacts);
    } catch (cause) {
      onError(fail(cause, '問い合わせを読み込めませんでした。'));
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  if (contacts === null) return <p className="page__notice">読み込んでいます…</p>;
  const shown = contacts.filter((c) => (openOnly ? c.status === 'open' : true));

  return (
    <section className="admin__panel">
      <label className="field field--check">
        <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
        <span>未対応だけ</span>
      </label>
      {shown.length === 0 && <p className="page__notice">{openOnly ? '未対応の問い合わせはありません。' : '問い合わせはまだありません。'}</p>}
      <ul className="admin__contacts">
        {shown.map((item) => (
          <li key={item.contactId} className="admin__contact">
            <p className="admin__contact-head">
              <span className="chip">{item.category}</span>
              <span>{new Date(item.createdAt).toLocaleString('ja-JP')}</span>
              <span className="admin__uid">{item.from}</span>
              {item.href && <Link to={item.href}>結果を見る</Link>}
              {item.status === 'done' && <span className="badge badge--done">対応済み</span>}
            </p>
            <p className="admin__contact-body">{item.message}</p>
            <p className="admin__contact-foot">
              <span>返信先: {item.replyTo ?? '（無し・対応済みで消えます）'}</span>
              <button className="button button--ghost button--small" type="button" disabled={working === item.contactId}
                onClick={() => {
                  const next = item.status === 'done' ? 'open' : 'done';
                  if (next === 'done' && !window.confirm('対応済みにします。返信先は消えます。')) return;
                  setWorking(item.contactId);
                  void updateAdminContact(item.contactId, next)
                    .then(load)
                    .catch((cause: unknown) => onError(fail(cause, '変えられませんでした。')))
                    .finally(() => setWorking(''));
                }}>
                {item.status === 'done' ? '未対応に戻す' : '対応済みにする'}
              </button>
            </p>
          </li>
        ))}
      </ul>
      <p className="page__footnote">
        送った人は uid の先頭だけを出しています。返信先は本人が書いたときだけ入り、対応済みにすると消えます。
      </p>
    </section>
  );
}

function LogsTab({ onError }: { readonly onError: (message: string) => void }) {
  const [logs, setLogs] = useState<readonly AdminLog[] | null>(null);

  useEffect(() => {
    void fetchAdminLogs().then((response) => setLogs(response.logs)).catch((cause: unknown) => onError(fail(cause, '記録を読み込めませんでした。')));
  }, [onError]);

  if (logs === null) return <p className="page__notice">読み込んでいます…</p>;
  return (
    <section className="admin__panel">
      <table className="admin__table">
        <thead><tr><th>いつ</th><th>だれが</th><th>なにを</th><th>対象</th><th>覚書</th></tr></thead>
        <tbody>
          {logs.map((log, index) => (
            <tr key={`${log.at}-${index}`}>
              <td>{new Date(log.at).toLocaleString('ja-JP')}</td>
              <td>{log.actor === 'budget' ? '予算の自動停止' : log.actor}</td>
              <td>{log.action}</td>
              <td className="admin__uid">{log.targetId}</td>
              <td>{log.note ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}


/**
 * 広告（doc/ADS.md §3）。AdSense とアフィリエイトの入り切りと、アフィリエイトの掲載物。
 *
 * - AdSense の出し先（`ca-pub-…` と広告ユニット）は環境変数（`ADSENSE_CLIENT` `ADSENSE_SLOTS`）。ここでは入り切りだけ。
 * - アフィリエイトは ASP の管理画面で取った **広告タグをそのまま貼れば**、飛び先・画像・計測用の画像を読み取る（`parseAdTag`）。
 *   保存するのは URL だけ（https）。HTML は保存しない。
 * - サイトに出るまで最大 30 秒かかる（サーバが設定を 30 秒持つため）。
 */
type Draft = Omit<AffiliateItem, 'id'> & { readonly id?: string };

const emptyDraft = (): Draft => ({
  network: 'a8', placements: ['result'], title: '', text: '', url: '', imageUrl: null, pixelUrl: null, enabled: true,
});

/**
 * ASP の広告タグ（HTML）から、飛び先・バナー画像・計測用の 1×1 画像を抜く。スクリプトは実行しない（DOMParser は実行しない）。
 * A8.net・バリューコマース・もしもアフィリエイトのバナー／テキスト広告の形（`<a href><img></a><img width=1 height=1>`）を想定。
 */
function parseAdTag(html: string): Partial<Draft> {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const link = doc.querySelector('a[href]');
  const images = [...doc.querySelectorAll('img[src]')];
  const isPixel = (img: Element) => img.getAttribute('width') === '1' || img.getAttribute('height') === '1';
  const banner = images.find((img) => !isPixel(img));
  const pixel = images.find(isPixel);
  const href = link?.getAttribute('href') ?? '';
  const network = /a8\.net/.test(href) ? 'a8'
    : /valuecommerce/.test(href) ? 'valuecommerce'
      : /moshimo/.test(href) ? 'moshimo'
        : /amazon\.|amzn\./.test(href) ? 'amazon'
          : /rakuten/.test(href) ? 'rakuten'
            : /afi-b|afb/.test(href) ? 'afb' : undefined;
  return {
    url: href,
    imageUrl: banner?.getAttribute('src') ?? null,
    pixelUrl: pixel?.getAttribute('src') ?? null,
    ...(network ? { network } : {}),
    ...(link?.textContent?.trim() ? { title: link.textContent.trim().slice(0, 60) } : {}),
  };
}

function AdsTab({ onError }: { readonly onError: (message: string) => void }) {
  const [ads, setAds] = useState<AdminAds | null>(null);
  const [adsense, setAdsense] = useState(false);
  const [affiliate, setAffiliate] = useState(false);
  const [items, setItems] = useState<Draft[]>([]);
  const [minPlays, setMinPlays] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');

  const apply = useCallback((value: AdminAds) => {
    setAds(value);
    setAdsense(value.adsense);
    setAffiliate(value.affiliate);
    setMinPlays(String(value.minPlays));
    setItems(value.items.map((item) => ({ ...item })));
  }, []);

  useEffect(() => {
    void fetchAdminAds().then(apply).catch((cause: unknown) => onError(fail(cause, '広告の設定を読み込めませんでした。')));
  }, [apply, onError]);

  if (!ads) return <p className="page__notice">読み込んでいます…</p>;

  const patch = (index: number, change: Partial<Draft>) =>
    setItems((list) => list.map((item, i) => (i === index ? { ...item, ...change } : item)));

  const save = () => {
    setBusy(true);
    setSaved('');
    void updateAdminAds({ adsense, affiliate, minPlays: Number(minPlays), items: items.map((item) => ({ ...item, imageUrl: item.imageUrl || null, pixelUrl: item.pixelUrl || null })) })
      .then((next) => { apply(next); setSaved('保存しました。30 秒ほどでサイトに出ます。'); })
      .catch((cause: unknown) => onError(fail(cause, '保存できませんでした。')))
      .finally(() => setBusy(false));
  };

  return (
    <section className="admin__panel">
      {!ads.featureEnabled && (
        <p className="page__error" role="status">
          このサイトは広告を切っています（環境変数 <code>FEATURE_ADS</code> が未設定）。ここで入れても、<strong>サイトには出ません</strong>。準備だけしておけます。
        </p>
      )}

      <h2 className="section__title">広告を出す診断</h2>
      <p className="page__lead">
        利用者が作った診断の入口・結果に広告を出すのは、<strong>受けた回数がこの数以上</strong>のものだけです。運営が取り込んだ診断は回数に関係なく出します。
        未対応の通報がある診断には出しません（「通報」タブ）。トップ・ヘルプには関係ありません。
      </p>
      <AdField label="受けた回数（0 ならすべての診断に出す）">
        <input className="field__input field__input--tiny" type="number" min={0} step={1} value={minPlays} disabled={busy} onChange={(e) => setMinPlays(e.target.value)} />
      </AdField>

      <h2 className="section__title">Google AdSense</h2>
      {ads.adsenseConfig ? (
        <p className="page__lead">
          パブリッシャー ID <code>{ads.adsenseConfig.client}</code>。広告ユニットを置いている場所:{' '}
          {Object.keys(ads.adsenseConfig.slots).length === 0
            ? 'なし（自動広告だけ）'
            : AD_PLACEMENTS.filter((p) => ads.adsenseConfig?.slots[p.id]).map((p) => p.label).join('・')}
          。変えるときは環境変数 <code>ADSENSE_CLIENT</code> <code>ADSENSE_SLOTS</code> を直して出し直します。
        </p>
      ) : (
        <p className="page__lead">環境変数 <code>ADSENSE_CLIENT</code>（<code>ca-pub-</code> から始まる ID）が未設定です。AdSense の審査を通ったら入れてください。</p>
      )}
      <label className="field field--check">
        <input type="checkbox" checked={adsense} disabled={busy || !ads.adsenseConfig} onChange={(e) => setAdsense(e.target.checked)} />
        <span>AdSense の広告を出す</span>
      </label>

      <h2 className="section__title">アフィリエイト</h2>
      <p className="page__lead">
        ASP（A8.net・もしもアフィリエイト・バリューコマースなど）の管理画面で取った<strong>広告タグを貼る</strong>と、飛び先と画像を読み取ります。
        どれにも「広告」「PR」の表示が付きます。<strong>1 つの場所に出るのは先頭から 3 件まで</strong>です。
      </p>
      <label className="field field--check">
        <input type="checkbox" checked={affiliate} disabled={busy} onChange={(e) => setAffiliate(e.target.checked)} />
        <span>アフィリエイトを出す</span>
      </label>

      <ul className="admin__themes">
        {items.map((item, index) => (
          <li key={item.id ?? `new-${index}`} className={`admin__theme${item.enabled ? '' : ' admin__row--deleted'}`}>
            <AdTagPaste disabled={busy} onParsed={(parsed) => patch(index, parsed)} />
            <div className="admin__limits">
              <AdField label="ASP">
                <select className="field__input" value={item.network} disabled={busy} onChange={(e) => patch(index, { network: e.target.value })}>
                  {AFFILIATE_NETWORKS.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
                </select>
              </AdField>
              <AdField label="見出し（60 字まで）">
                <input className="field__input" value={item.title} maxLength={60} disabled={busy} onChange={(e) => patch(index, { title: e.target.value })} />
              </AdField>
              <AdField label="ひとこと（200 字まで・任意）">
                <input className="field__input" value={item.text} maxLength={200} disabled={busy} onChange={(e) => patch(index, { text: e.target.value })} />
              </AdField>
              <AdField label="飛び先（https）">
                <input className="field__input" type="url" value={item.url} disabled={busy} onChange={(e) => patch(index, { url: e.target.value })} />
              </AdField>
              <AdField label="バナー画像（https・任意）">
                <input className="field__input" type="url" value={item.imageUrl ?? ''} disabled={busy} onChange={(e) => patch(index, { imageUrl: e.target.value })} />
              </AdField>
              <AdField label="計測用の画像（https・任意）">
                <input className="field__input" type="url" value={item.pixelUrl ?? ''} disabled={busy} onChange={(e) => patch(index, { pixelUrl: e.target.value })} />
              </AdField>
            </div>
            <fieldset className="admin__actions" disabled={busy}>
              <legend className="field__label">出す場所</legend>
              {AD_PLACEMENTS.map((place) => (
                <label key={place.id} className="field field--check">
                  <input type="checkbox" checked={item.placements.includes(place.id)}
                    onChange={(e) => patch(index, {
                      placements: e.target.checked
                        ? [...item.placements, place.id]
                        : item.placements.filter((p): p is AdPlacement => p !== place.id),
                    })} />
                  <span>{place.label}</span>
                </label>
              ))}
            </fieldset>
            <div className="admin__actions">
              <label className="field field--check">
                <input type="checkbox" checked={item.enabled} disabled={busy} onChange={(e) => patch(index, { enabled: e.target.checked })} />
                <span>出す</span>
              </label>
              <button className="button button--ghost button--small" type="button" disabled={busy || index === 0}
                onClick={() => setItems((list) => { const next = [...list]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next; })}>
                上へ
              </button>
              <button className="button button--danger button--small" type="button" disabled={busy}
                onClick={() => setItems((list) => list.filter((_, i) => i !== index))}>
                外す
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="page__actions">
        <button className="button button--ghost" type="button" disabled={busy} onClick={() => setItems((list) => [...list, emptyDraft()])}>
          掲載物を足す
        </button>
        <button className="button button--primary" type="button" onClick={save} disabled={busy}>
          {busy ? '保存しています…' : '保存する'}
        </button>
        {saved && <span className="admin__saved" role="status">{saved}</span>}
      </div>
      {ads.updatedAt && (
        <p className="page__footnote">{new Date(ads.updatedAt).toLocaleString('ja-JP')} に {ads.updatedBy ?? '管理者'} が変えました。</p>
      )}
    </section>
  );
}

function AdField({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
    </label>
  );
}

/** 広告タグの貼り付け欄。貼ったら読み取って上の欄を埋める。 */
function AdTagPaste({ disabled, onParsed }: { readonly disabled: boolean; readonly onParsed: (parsed: Partial<Draft>) => void }) {
  const [html, setHtml] = useState('');
  return (
    <div className="admin__search">
      <textarea className="field__input" rows={2} value={html} disabled={disabled} placeholder="ASP の広告タグ（HTML）を貼る"
        onChange={(e) => setHtml(e.target.value)} />
      <button className="button button--ghost button--small" type="button" disabled={disabled || html.trim() === ''}
        onClick={() => { onParsed(parseAdTag(html)); setHtml(''); }}>
        読み取る
      </button>
    </div>
  );
}
